/**
 * De/para entre o **nome canônico** dos campos da Ficha de Cadastro Individual do e-SUS APS e os
 * campos do formulário de paciente.
 *
 * FONTE ÚNICA (D2 do plano). Três consumidores leem esta tabela e nenhum reimplementa o mapeamento:
 *  1. o schema dinâmico (`validation/pacienteSchema.ts`), que liga `required` a partir de
 *     `camposObrigatorios[].campo` devolvido por `GET /empresas/{id}/esus/cadastro-paciente`;
 *  2. a tradução do 422 (`_fciErro.ts`), que leva `erros[].campo` ao campo certo do formulário;
 *  3. a montagem do payload de criação, via `enviarNaCriacao`.
 *
 * PROCEDÊNCIA das 14 chaves: `br.com.lauduzone.auth.esus.CampoObrigatorioFci` (backend, fatia F3).
 * O nome canônico **não é** o caminho JSON de nenhum endpoint: em `POST /pacientes/empresa/{id}` os
 * campos de pessoa chegam na raiz do corpo (`cpf`, `sexo`, `nascimento`), porque `PacienteDtoCreate`
 * herda de `UsuarioDtoCreate`.
 *
 * Esta tabela é de **nomes**, nunca de regra: quando um campo é exigido continua sendo decidido pelo
 * endpoint da F3.
 */

import { DocumentoPessoaType } from '../../../../../../_metronic/helpers'

/** Nome canônico de um campo da FCI, no formato pontilhado do enum do backend. */
export type CampoCanonicoFci =
  | 'pessoa.sexo'
  | 'pessoa.dataNascimento'
  | 'pessoa.cpfOuCns'
  | 'pessoa.telefoneCelular'
  | 'dadosAdicionais.nomeMae'
  | 'dadosAdicionais.raca'
  | 'dadosAdicionais.etnia'
  | 'dadosAdicionais.nacionalidade'
  | 'dadosAdicionais.dtNaturalizacao'
  | 'dadosAdicionais.portariaNaturalizacao'
  | 'dadosAdicionais.paisNascimento'
  | 'dadosAdicionais.dtEntradaBrasil'
  | 'dadosAdicionais.municipioNascimento'
  | 'dadosAdicionais.justificativaNaoPossuiCpf'

export type EntradaCampoFci = {
  /**
   * Campo do formik. `null` quando o campo depende do estado do formulário e precisa de
   * `resolverCampoFormik` (é o caso de `pessoa.cpfOuCns`, que cai em `cpf` ou em `cns`).
   */
  campoFormik: string | null
  /** Rótulo em pt-BR, usado no resumo de erros e nas mensagens de desfecho parcial. */
  rotulo: string
  /**
   * O campo vai no corpo de `POST /pacientes/empresa/{id}`?
   *
   * Todos os campos da ficha são `true` desde que a `tasks/esus-fci-f2d-celular-na-criacao.md`
   * (`done`) fez `PacienteDtoCreate`/`UsuarioDtoCadastro` aceitarem o celular na criação. O booleano
   * fica porque é o **único** ponto que decide o corpo do POST — nenhum `if` espalhado pela tela.
   */
  enviarNaCriacao: boolean
  /**
   * O que conta como "preenchido" quando o campo canônico é satisfeito por **mais de um** campo do
   * formulário.
   *
   * Isto **não** é a regra de quando o campo é exigido — essa continua vindo do endpoint da F3.
   * É a leitura do predicado `preenchido` do enum do backend, que o contrato não publica: a ficha
   * aceita nome da mãe **ou** a marca de desconhecido, e CPF **ou** CNS. Sem isto a tela barraria um
   * cadastro que o backend aceita.
   */
  satisfeitoPor?: (values: any) => boolean
}

export const CAMPOS_FCI: Record<CampoCanonicoFci, EntradaCampoFci> = {
  'pessoa.sexo': { campoFormik: 'sexo', rotulo: 'Sexo', enviarNaCriacao: true },
  'pessoa.dataNascimento': {
    campoFormik: 'nascimento',
    rotulo: 'Data de nascimento',
    enviarNaCriacao: true,
  },
  'pessoa.cpfOuCns': {
    campoFormik: null,
    rotulo: 'CPF ou CNS',
    enviarNaCriacao: true,
    // `snapshot -> !semCpf || comCns` no enum do backend: um dos dois documentos basta.
    satisfeitoPor: (values) => Boolean(values?.cpf) || Boolean(values?.cns),
  },
  'pessoa.telefoneCelular': {
    campoFormik: 'telefoneCelular',
    rotulo: 'Telefone celular',
    // F2d (`done`): o contrato de criação aceita o celular. Com `false` aqui, o valor digitado era
    // descartado do POST e o validador do backend devolvia 422 exigindo o campo que a tela mostrava
    // preenchido.
    enviarNaCriacao: true,
  },
  'dadosAdicionais.nomeMae': {
    campoFormik: 'nomeMae',
    rotulo: 'Nome da mãe',
    enviarNaCriacao: true,
    // `snapshot -> naoVazio(nomeMae) || desconheceNomeMae` no enum do backend.
    satisfeitoPor: (values) =>
      Boolean(String(values?.nomeMae ?? '').trim()) || values?.desconheceNomeMae === true,
  },
  'dadosAdicionais.raca': { campoFormik: 'raca', rotulo: 'Raça/Cor', enviarNaCriacao: true },
  'dadosAdicionais.etnia': { campoFormik: 'etnia', rotulo: 'Etnia', enviarNaCriacao: true },
  'dadosAdicionais.nacionalidade': {
    campoFormik: 'nacionalidade',
    rotulo: 'Nacionalidade',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.dtNaturalizacao': {
    campoFormik: 'dtNaturalizacao',
    rotulo: 'Data de naturalização',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.portariaNaturalizacao': {
    campoFormik: 'portariaNaturalizacao',
    rotulo: 'Portaria de naturalização',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.paisNascimento': {
    campoFormik: 'paisNascimento',
    rotulo: 'País de nascimento',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.dtEntradaBrasil': {
    campoFormik: 'dtEntradaBrasil',
    rotulo: 'Data de entrada no Brasil',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.municipioNascimento': {
    campoFormik: 'municipioNascimento',
    rotulo: 'Município de nascimento',
    enviarNaCriacao: true,
  },
  'dadosAdicionais.justificativaNaoPossuiCpf': {
    campoFormik: 'justificativaNaoPossuiCpf',
    rotulo: 'Motivo de não possuir CPF',
    enviarNaCriacao: true,
  },
}

/** Estado do formulário de que o de/para precisa para resolver campos dependentes. */
export type ContextoFormularioFci = {
  /** Tipo de documento escolhido no combo `documento`. */
  tipoDocumento?: DocumentoPessoaType
}

/**
 * Campo do formik para um campo canônico, resolvendo os dependentes do estado da tela.
 *
 * `pessoa.cpfOuCns` cai no campo de documento **em uso**: `cns` quando o tipo selecionado é CNS,
 * `cpf` nos demais casos (é o campo que a ficha vai ler e onde o operador corrige).
 * Devolve `null` para campo canônico desconhecido — quem chama trata como "sem destino" e mostra a
 * correção no resumo, em vez de engolir a mensagem.
 */
export const resolverCampoFormik = (
  campo: string,
  contexto: ContextoFormularioFci = {}
): string | null => {
  if (campo === 'pessoa.cpfOuCns') {
    return contexto.tipoDocumento === DocumentoPessoaType.Cns ? 'cns' : 'cpf'
  }
  const entrada = CAMPOS_FCI[campo as CampoCanonicoFci]
  return entrada ? entrada.campoFormik : null
}

/** Rótulo pt-BR de um campo canônico; `undefined` quando o campo não está na tabela. */
export const rotuloCampoFci = (campo: string): string | undefined =>
  CAMPOS_FCI[campo as CampoCanonicoFci]?.rotulo

/** O campo canônico entra no corpo de criação? Campo desconhecido responde `false`. */
export const enviarNaCriacao = (campo: CampoCanonicoFci): boolean =>
  CAMPOS_FCI[campo]?.enviarNaCriacao === true

/** Como saber se o campo canônico já está satisfeito pelo estado atual do formulário. */
export const satisfeitoPorFci = (campo: string): ((values: any) => boolean) | undefined =>
  CAMPOS_FCI[campo as CampoCanonicoFci]?.satisfeitoPor
